import { useState } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { useToast } from "./components/userToast";
import { BASE_URL } from "./baseurl";

function LabBackground() {
  return (
    <svg
      className="absolute inset-0 w-full h-full z-0"
      viewBox="0 0 1440 900"
      preserveAspectRatio="xMidYMid slice"
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        <radialGradient id="bgGlow" cx="50%" cy="30%" r="80%">
          <stop offset="0%" stopColor="#0f2e28" />
          <stop offset="55%" stopColor="#0a1512" />
          <stop offset="100%" stopColor="#050807" />
        </radialGradient>
        <pattern id="grid" width="60" height="60" patternUnits="userSpaceOnUse">
          <path d="M60 0H0V60" fill="none" stroke="#14b8a6" strokeOpacity="0.06" strokeWidth="1" />
        </pattern>
      </defs>

      <rect width="1440" height="900" fill="url(#bgGlow)" />
      <rect width="1440" height="900" fill="url(#grid)" />

      <g stroke="#14b8a6" strokeOpacity="0.18" strokeWidth="1.5" fill="none">
        <line x1="180" y1="120" x2="340" y2="220" />
        <line x1="340" y1="220" x2="300" y2="380" />
        <line x1="340" y1="220" x2="500" y2="180" />
        <line x1="1120" y1="700" x2="1260" y2="600" />
        <line x1="1260" y1="600" x2="1260" y2="440" />
        <line x1="1260" y1="600" x2="1380" y2="680" />
      </g>
      <g fill="#14b8a6" fillOpacity="0.35">
        <circle cx="180" cy="120" r="5" />
        <circle cx="340" cy="220" r="7" />
        <circle cx="300" cy="380" r="4" />
        <circle cx="500" cy="180" r="4" />
        <circle cx="1120" cy="700" r="4" />
        <circle cx="1260" cy="600" r="7" />
        <circle cx="1260" cy="440" r="4" />
        <circle cx="1380" cy="680" r="5" />
      </g>
    </svg>
  );
}

export default function ResetPassword() {
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();

  const emailFromState = location.state?.email || "";

  const [email, setEmail] = useState(emailFromState);
  const [otp, setOtp] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (password !== confirmPassword) {
      toast({
        variant: "error",
        title: "Passwords don't match",
        description: "Please make sure both passwords are the same.",
      });
      return;
    }

    if (password.length < 8) {
      toast({
        variant: "error",
        title: "Password too short",
        description: "Password must be at least 8 characters.",
      });
      return;
    }

    setLoading(true);
    try {
      const { data } = await axios.post(`${BASE_URL}/auth/reset-password`, {
        email,
        otp,
        newPassword: password,
      });
      toast({
        variant: "success",
        title: "Password reset",
        description: data.message || "Your password has been updated.",
      });
      setSubmitted(true);
      setTimeout(() => navigate("/login"), 1200);
    } catch (err) {
      toast({
        variant: "error",
        title: "Something went wrong",
        description: err.response?.data?.message || "Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="relative w-full min-h-screen flex items-center justify-center overflow-hidden bg-[#050807]">
      <LabBackground />
      <div className="absolute inset-0 bg-black/30 z-10" />

      <div className="relative z-20 w-full max-w-[420px] mx-6 bg-black/50 backdrop-blur-md border border-white/10 rounded-2xl p-10">
        <div className="flex flex-col items-center mb-8">
        <div className="h-24 w-24 rounded-full overflow-hidden flex items-center justify-center bg-teal-500">
  <img 
    src="/logo.png" 
    alt="Futura Bio Labs" 
    className="w-full h-full object-cover"
  />
</div>
          <p className="text-gray-300 text-[15px] mt-2 text-center">
            {submitted
              ? "Your password has been reset"
              : "Enter the code and your new password"}
          </p>
        </div>

        {!submitted ? (
          <form onSubmit={handleSubmit} className="flex flex-col gap-5">
            <div>
              <label className="block text-gray-300 text-sm font-medium mb-2">
                Email
              </label>
              <input
                type="email"
                name="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="you@example.com"
                className="w-full bg-white/5 border border-white/20 rounded-full px-5 py-3 text-white text-sm placeholder:text-gray-500 focus:outline-none focus:border-teal-500 focus:ring-1 focus:ring-teal-500"
              />
            </div>

            <div>
              <label className="block text-gray-300 text-sm font-medium mb-2">
                Reset Code
              </label>
              <input
                type="text"
                name="otp"
                value={otp}
                onChange={(e) => setOtp(e.target.value)}
                required
                placeholder="Enter the code from your email"
                className="w-full bg-white/5 border border-white/20 rounded-full px-5 py-3 text-white text-sm placeholder:text-gray-500 focus:outline-none focus:border-teal-500 focus:ring-1 focus:ring-teal-500"
              />
            </div>

            <div>
              <label className="block text-gray-300 text-sm font-medium mb-2">
                New Password
              </label>
              <input
                type="password"
                name="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                placeholder="••••••••"
                className="w-full bg-white/5 border border-white/20 rounded-full px-5 py-3 text-white text-sm placeholder:text-gray-500 focus:outline-none focus:border-teal-500 focus:ring-1 focus:ring-teal-500"
              />
            </div>

            <div>
              <label className="block text-gray-300 text-sm font-medium mb-2">
                Confirm Password
              </label>
              <input
                type="password"
                name="confirmPassword"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                placeholder="••••••••"
                className="w-full bg-white/5 border border-white/20 rounded-full px-5 py-3 text-white text-sm placeholder:text-gray-500 focus:outline-none focus:border-teal-500 focus:ring-1 focus:ring-teal-500"
              />
              <p className="text-gray-500 text-xs mt-2">
                Must be at least 8 characters.
              </p>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="gold-glow-btn w-full bg-white hover:bg-gray-200 text-black font-semibold text-[15px] rounded-full py-3 mt-2 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {loading ? "RESETTING..." : "RESET PASSWORD"}
            </button>
          </form>
        ) : (
          <div className="flex flex-col items-center text-center gap-4">
            <div className="w-14 h-14 rounded-full bg-teal-500/20 border border-teal-500/40 flex items-center justify-center">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" className="text-teal-400" xmlns="http://www.w3.org/2000/svg">
                <path d="M5 13l4 4L19 7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <p className="text-gray-300 text-sm">
              Your password has been successfully reset. Redirecting you to sign in...
            </p>
          </div>
        )}

        <p className="text-center text-gray-400 text-sm mt-7">
          Remembered it?{" "}
          <NavLink to="/login" className="text-teal-400 hover:text-teal-300 font-medium">
            Back to sign in
          </NavLink>
        </p>
      </div>
    </section>
  );
}